import { computed, ref, watch } from "vue"
import { acceptHMRUpdate, defineStore } from "pinia"
import { useI18n } from "vue-i18n"

import { localStorageService } from "@/services/localStorage.service"
import BillingInfoService from "@/services/billingInfo.service"
import AccountDetailService from "@/services/accountDetail.service"
import PlanPriceService from "@/services/planPrice.service"
import AccountService from "@/services/account.service"
import PaymentSourceService from "@/services/paymentSource.service"

import useToastManager from "@/composables/toastManager"
import { iriToUuid } from "@/services/helper"
import { buildQueryString, toUuid } from "@/services/utils"
import UserAccountService from "@/services/userAccount.service.js"
import groupsService from "@/services/groups.service.js"
import { useAbility } from "@casl/vue"
import abilityRules from "@/roles/rules"
import { useRoute, useRouter } from "vue-router"
import { useLoaderStore } from "@/stores/loader.js"
import { useAuthentication } from "@/composables/useAuthentication.js"

export const useUserAccountsStore = defineStore("userAccounts", () => {
  const userAccount = ref(null)
  const userAccountDetail = ref(null)
  const userPlan = ref(null)
  const isLoading = ref(false)
  const userBillingInfo = ref(null)
  const userPaymentMethod = ref(null)
  const userInvoices = ref(null)
  const ability = useAbility()
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()

  const isMethodExpiring = computed(() => userAccount.value?.status === 6)
  const isMethodExpired = computed(() => userAccount.value?.status === 7)
  const userIsLoggedIn = computed(() => Boolean(localStorageService.getUserUuid()))
  const accountFeatures = computed(() => userAccount.value?.features)

  /*
    users & roles state
   */
  const workspacesMembers = ref([])
  const currentWorkSpace = ref(null)
  const workspaces = ref([])
  const groups = ref([])
  const role = ref(null)
  const workspaceName = computed(() => currentWorkSpace.value?.name)
  const currentWorkspaceAccountIri = computed(() => currentWorkSpace.value?.account?.iri)
  const workspaceLogo = computed(() => currentWorkSpace.value?.logo)
  const planUsersLimit = computed(() => userPlan.value?.features?.limits?.user)
  const getOwnerMembers = computed(() =>
    workspacesMembers.value.reduce(
      (acc, workSpace) => {
        const key = workSpace.isOwner ? "owner" : "members"
        acc[key] = acc[key] ? [...acc[key], workSpace] : [workSpace]
        return acc
      },
      { members: [], owner: [] }
    )
  )

  /*
    users & roles actions
   */
  async function init(invite_id) {
    try {
      useLoaderStore().showLoader()
      if (localStorageService.getUserUuid()) {
        await fetchWorkspaces(invite_id)
      } else {
        console.warn("User account IRI not found in INIT method from userAccounts store")
        useAuthentication().logout()
        return
      }

      if (currentWorkSpace.value) {
        await loadUserAccount()
      }
    } catch (error) {
      console.error(`Error init userAccount ${error}`)
    } finally {
      useLoaderStore().hideLoader()
    }
  }

  function setUserAccountIri() {
    localStorageService.setUserAccountIri(currentWorkSpace.value.iri)
  }

  async function loadUserAccount(options = { force: false }) {
    try {
      useLoaderStore().showLoader()
      if (localStorageService.getUserUuid() && currentWorkSpace.value) {
        setUserAccountIri()
        await setUserAccount(options)
        await Promise.all([
          getGroups(),
          fetchWorkspacesMembers(),
          setUserPlan(options),
          setUserBillingInfo(options)
        ])
      }
    } catch (error) {
      console.error(`Error loadUser ${error}`)
    } finally {
      useLoaderStore().hideLoader()
    }
  }

  async function getGroups() {
    groups.value = await groupsService.getCollection()
    const group = groups.value.items.find((group) => group.iri === currentWorkSpace.value.group)
    role.value = null

    if (group) {
      role.value = {
        name: group.name,
        subscription: userAccount.value.subscriptionTypeDescription,
        currentTrialDays: userAccount.value.currentTrialDays,
        permissions: group.permissions
      }
    }
  }

  async function fetchWorkspaces(inviteId) {
    const { items } = await UserAccountService.getCollection(null, {
      needsAuth: true,
      queryString: buildQueryString({ "user.uuid": localStorageService.getUserUuid() })
    })

    workspacesMembers.value = [...items]
    workspaces.value = items.sort((a, b) => b.isOwner - a.isOwner)

    currentWorkSpace.value = inviteId
      ? workspaces.value.find(({ invitation }) => invitation?.uuid === inviteId)
      : workspaces.value[0]

    setUserAccountIri()
  }

  async function fetchWorkspacesMembers() {
    const { items } = await UserAccountService.getCollection(null, {
      needsAuth: true,
      queryString: buildQueryString({
        pagination: false,
        "account.uuid": currentWorkSpace.value?.account?.uuid
      })
    })

    workspacesMembers.value = [...items]
  }

  async function changeWorkSpace(newWorkSpace) {
    currentWorkSpace.value = newWorkSpace
      ? newWorkSpace
      : workspaces.value.find(({ isOwner }) => isOwner)
    await loadUserAccount({ force: true })
    await router.push("/")
  }

  /*
  users and roles watch
   */
  watch(role, () => {
    ability.update(abilityRules(role.value).rules)

    if (!role.value) return

    if (route.name) {
      if (ability.cannot("read", route.name)) {
        router.push({ name: "qrs" })
      }
    }
  })

  /*
  others actions
   */
  async function setUserAccount() {
    try {
      userAccount.value = await AccountService.getItem(
        iriToUuid(currentWorkSpace.value.account.uuid)
      )
      await setUserAccountDetail()
      await setUserPlan()
    } catch (error) {
      console.error(`Error setUserAccount ${error}`)
    }
  }

  async function setUserAccountDetail() {
    try {
      const accountDetail = await AccountDetailService.getItem(userAccount.value?.detail.uuid)

      if (accountDetail) {
        userAccountDetail.value = accountDetail
      }
    } catch (error) {
      console.error(`Error setUserAccountDetail ${error}`)
    }
  }

  async function setUserPlan(options = { force: false }) {
    if (userPlan.value && !options.force) return

    try {
      const planPriceIri = userAccount.value?.planPrice
      const response = await PlanPriceService.getItem(iriToUuid(planPriceIri))

      if (response) {
        const { plan, ...price } = response
        userPlan.value = { ...plan, price }
      }
    } catch (error) {
      console.error(`Error setUserPlan ${error}`)
    }
  }

  async function setUserBillingInfo(options = { force: false }) {
    if (userBillingInfo.value && !options.force) return
    if (!userAccount.value?.billingInfo) return
    try {
      const response = await BillingInfoService.getItem(iriToUuid(userAccount.value?.billingInfo))
      if (response) userBillingInfo.value = response
    } catch (error) {
      console.error(`Error setUserBillingInfo ${error}`)
    }
  }

  async function downgradeToFreePlan(plan) {
    try {
      await AccountService.downgradePlan(userAccount.value?.iri, plan.iri)
      await loadUserAccount({ force: true })
    } catch (error) {
      console.error(`Error downgradePlan ${error}`)
      useToastManager().error({
        message: t("component.alert.contact.support.label")
      })

      return Promise.reject(error)
    }
  }

  async function changeAccountAvatar(file) {
    userAccountDetail.value = await AccountDetailService.updateLogo(
      userAccountDetail.value.uuid,
      file
    )
    await fetchWorkspaces()
  }

  async function updateAccountDetails(payload) {
    if (payload?.accountName) currentWorkSpace.value.name = payload.accountName
    userAccountDetail.value = await AccountDetailService.update(userAccountDetail.value.uuid, {
      payload
    })
    return Promise.resolve()
  }

  async function createOrUpdateBillingInfo(payload) {
    const billingInfoUpdated = userAccount.value?.billingInfo
      ? await BillingInfoService.update(iriToUuid(userAccount.value?.billingInfo), { payload })
      : await BillingInfoService.create({
          payload: { ...payload, account: userAccount.value?.iri }
        })

    userBillingInfo.value = billingInfoUpdated
    return Promise.resolve(billingInfoUpdated)
  }

  function resetState() {
    userAccount.value = null
    userPlan.value = null
    userBillingInfo.value = null
    userPaymentMethod.value = null
    userInvoices.value = null
  }

  async function loadUserPaymentMethod() {
    if (userAccount.value?.paymentSource) {
      const response = await PaymentSourceService.getItem(toUuid(userAccount.value?.paymentSource))
      userPaymentMethod.value = response
    }
  }

  async function pollForUpdatedPaymentSource(paymentSourceIri, cb) {
    await PaymentSourceService.pollForUpdatedPaymentSource(
      toUuid(paymentSourceIri),
      userPaymentMethod.value.cardInfo?.created,
      (message, paymentMethod) => {
        userPaymentMethod.value = paymentMethod
        cb(message)
      }
    )
  }

  return {
    userAccount,
    accountFeatures,
    workspaceName,
    userAccountDetail,
    userPlan,
    userBillingInfo,
    userPaymentMethod,
    userInvoices,
    isMethodExpiring,
    isMethodExpired,
    userIsLoggedIn,
    planUsersLimit,
    currentWorkSpace,
    workspaces,
    groups,
    workspacesMembers,
    getOwnerMembers,
    role,
    isLoading,
    workspaceLogo,
    currentWorkspaceAccountIri,
    init,
    fetchWorkspacesMembers,
    changeWorkSpace,
    pollForUpdatedPaymentSource,
    setUserAccount,
    loadUserAccount,
    downgradeToFreePlan,
    changeAccountAvatar,
    updateAccountDetails,
    resetState,
    fetchWorkspaces,
    setUserAccountDetail,
    createOrUpdateBillingInfo,
    loadUserPaymentMethod
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserAccountsStore, import.meta.hot))
}
